import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { MapPin, Users, TrendingUp, Loader, ChevronRight, Rocket } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPORT_LABELS = { tennis: "Tennis", pickleball: "Pickleball", golf: "Golf", padel: "Padel", squash: "Squash", badminton: "Badminton" };

const STATUS_STYLES = {
  active: "bg-brand-green/15 text-brand-green border-brand-green/40",
  healthy: "bg-brand-green/15 text-brand-green border-brand-green/40",
  seeding: "bg-cyan-500/15 text-cyan-300 border-cyan-400/40",
  private_beta: "bg-blue-500/15 text-blue-300 border-blue-400/40",
  waitlist: "bg-amber-500/15 text-amber-300 border-amber-400/40",
};

export default function MarketsPage() {
  const [markets, setMarkets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/markets`);
        if (r.ok) setMarkets((await r.json()).markets || []);
      } finally { setLoading(false); }
    })();
  }, []);

  const byCity = markets.reduce((acc, m) => { (acc[m.city] = acc[m.city] || []).push(m); return acc; }, {});

  return (
    <div className="min-h-screen bg-[#0F1629] pt-24 pb-16 dark-section" data-testid="markets-page">
      <div className="max-w-5xl mx-auto px-4">
        <div className="mb-10 max-w-2xl">
          <p className="text-xs text-brand-green font-semibold uppercase tracking-wider mb-2">Expansion</p>
          <h1 className="text-4xl sm:text-5xl font-heading font-black text-white mb-3">Where we're headed next</h1>
          <p className="text-gray-400">ActivitySports launches city by city, sport by sport. Join the waitlist for your city and we'll notify you the moment partners and sessions go live near you.</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader size={28} className="text-brand-green animate-spin" /></div>
        ) : (
          <div className="space-y-8">
            {Object.entries(byCity).map(([city, list]) => (
              <div key={city}>
                <h2 className="text-lg font-bold text-white flex items-center gap-2 mb-3"><MapPin size={17} className="text-brand-green" /> {city}</h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {list.map((m) => (
                    <Link key={m.id} to={`/markets/${m.id}`}
                      className="group bg-[#161B28] border border-white/8 rounded-2xl p-5 hover:border-brand-green/25 transition-all dark-section"
                      data-testid="market-card">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-white font-bold">{SPORT_LABELS[m.sport] || m.sport}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLES[m.status] || STATUS_STYLES.waitlist}`}>{m.status_label}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-400 mb-4">
                        <Users size={14} /> {m.waitlist_count} on waitlist
                      </div>
                      <span className="inline-flex items-center gap-1 text-sm text-brand-green group-hover:gap-2 transition-all">
                        {m.status === "waitlist" ? "Join waitlist" : "View market"} <ChevronRight size={14} />
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
            {markets.length === 0 && (
              <div className="text-center py-16 text-gray-400 flex flex-col items-center gap-3">
                <Rocket size={32} className="text-gray-600" /> New markets coming soon.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
